if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.stockNotify = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        /*
          product-info.js's updateMedia() cannot flip the popup's active image on its own:

          - Its add/remove loops only diff slides by data-media-id. preventDuplicatedIDs()
            renames the modal's to `quickadd-…` while the fetched ones stay un-prefixed, so
            the FIRST variant change replaces the whole slide set (arriving with the server's
            correct `is-active`) and every change after that sees source ids == fetched ids,
            diffs to nothing, and leaves the DOM — and `is-active` — untouched.
          - Its setActiveMedia() call, which normally does the flipping, looks up
            `${this.dataset.section}-${mediaId}` — the `quickadd-`-prefixed id, absent from
            the DOM after that first swap. (That call is also a no-op here by design; see
            ensureRequiredAssets.)

          So own the flip: variantChange publishes after updateMedia() has settled the slide
          set, and carries the variant whose featured_media we need.
        */
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!this.hasAttribute('open')) return;

          // Mirrors product-info.js's own `sectionId` getter, which is what it publishes.
          const productInfo = this.modalContent?.querySelector('product-info');
          const sectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;
          if (!productInfo || data?.sectionId !== sectionId) return;

          this.updateActiveMedia(data.variant?.featured_media?.id);
          this.syncStockNotifyVariant(data.variant);
        });

        /*
          Thumbnails are normally wired by media-gallery.js, which the popup can't use (see
          ensureRequiredAssets). Delegating from modalContent instead of binding per
          thumbnail means one listener covers every open and survives product-info.js
          replacing the gallery markup underneath.
        */
        this.modalContent?.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('.pi-gallery__thumb[data-target]');
          if (!thumbnail) return;

          event.preventDefault();
          // data-target is `<section>-<mediaId>`; the media id is the trailing segment.
          this.updateActiveMedia(thumbnail.dataset.target.split('-').pop());
        });
      }

      /*
        Matches slides and thumbnails on the `-<mediaId>` suffix rather than a whole id.
        preventDuplicatedIDs() prefixes the popup's ids with `quickadd-`, but updateMedia()
        later swaps in un-prefixed slides straight from the server while leaving the
        thumbnail strip's prefixed `data-target`s alone — so the two halves of the gallery
        disagree on their ids, and only the shared media id is dependable.
      */
      updateActiveMedia(mediaId) {
        if (!mediaId) return;

        const matchesMedia = (value) => value?.endsWith(`-${mediaId}`);
        const slides = Array.from(this.modalContent.querySelectorAll('.pi-gallery__media-item'));
        const activeSlide = slides.find((slide) => matchesMedia(slide.dataset.mediaId));
        if (!activeSlide) return;

        slides.forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));

        const thumbnails = Array.from(this.modalContent.querySelectorAll('.pi-gallery__thumb[data-target]'));
        const activeThumbnail = thumbnails.find((thumbnail) => matchesMedia(thumbnail.dataset.target));

        thumbnails.forEach((thumbnail) => {
          const button = thumbnail.querySelector('button');
          if (!button) return;

          if (thumbnail === activeThumbnail) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });

        this.scrollThumbnailIntoView(activeThumbnail);
      }

      // Scrolls the strip itself rather than calling scrollIntoView, which would also pan
      // the modal's own scroll container when the thumbnail sits below the fold.
      scrollThumbnailIntoView(thumbnail) {
        const strip = thumbnail?.parentElement;
        if (!strip) return;

        const stripBox = strip.getBoundingClientRect();
        const thumbnailBox = thumbnail.getBoundingClientRect();

        if (thumbnailBox.left < stripBox.left || thumbnailBox.right > stripBox.right) {
          strip.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.teardownStockNotify();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        Promise.all([
          fetch(opener.getAttribute('data-product-url')).then((response) => response.text()),
          this.ensureRequiredAssets(),
        ])
          .then(([responseText]) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.setupStockNotify(responseHTML);
            this.setupWishlist();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // The fetched <product-info> markup is the real product page, which in this theme
      // wraps breadcrumbs + the full media gallery (see main-product.liquid) — none of that
      // is meant for the modal, but the price/variant/buy-button styles it shares with the
      // real page (section-product-info.css, product-info.js, pi-variant-dropdown.js) are
      // only enqueued there too. Collection/related-product sections never load them, so
      // load them here, once, lazily, the first time a quick-add modal actually opens.
      // Deliberately does NOT load media-gallery.js. On a variant change, updateMedia()
      // swaps in the freshly fetched server slides, which already carry `is-active` on the
      // right one (pi-media-gallery.liquid), and CSS shows only that slide. If
      // media-gallery.js were loaded, updateMedia()'s setActiveMedia() call would then
      // clobber that with a lookup for `${this.dataset.section}-${mediaId}` — but
      // preventDuplicatedIDs() has renamed the modal's ids to `quickadd-…` while the
      // fetched slides are un-prefixed, so the lookup misses, falls back to "first slide",
      // and prepends it — pinning every later variant change to that same image. Leaving
      // setActiveMedia undefined makes product-info.js's optional-chained call a no-op, and
      // updateActiveMedia() takes over the job it would have done, for both variant changes
      // and thumbnail clicks. (The thumbnail strip's own arrows need no extra asset:
      // <slider-component> comes from global.js, which every page already loads.)
      ensureRequiredAssets() {
        return Promise.all([
          this.loadStylesheet(this.dataset.productInfoCss),
          this.loadScript(this.dataset.productInfoScript),
          this.loadScript(this.dataset.variantDropdownScript),
          // Moves the option the shopper did not touch onto a value that exists with the one they
          // did, exactly as on the product page — without it, Size 4.5kg + a flavour only made in
          // 2.27kg lands on "Unavailable" with no price or add to cart.
          this.loadScript(this.dataset.variantGuardScript),
        ]);
      }

      loadStylesheet(href) {
        if (!href || document.querySelector(`link[href="${href}"]`)) return Promise.resolve();

        return new Promise((resolve) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = resolve;
          link.onerror = resolve;
          document.head.appendChild(link);
        });
      }

      loadScript(src) {
        if (!src || document.querySelector(`script[src="${src}"]`)) return Promise.resolve();

        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = resolve;
          document.head.appendChild(script);
        });
      }

      /*
        The back-in-stock app, made to work inside the popup.

        Two things stop it on its own. Its loader script sits OUTSIDE the <product-info> element
        this modal is built from, so on a collection page the app is never loaded at all — which is
        why a sold-out variant picked here showed nothing. And once loaded it resolves the variant
        with document.querySelector("form[action='/cart/add']"): the first such form in the
        document, which on a collection page is a product card's, not the popup's. Left alone it
        would sign the shopper up for whatever that card happens to be selling.

        The app checks the element its own <script data-app> tag sits in before it falls back to
        that query, though, and only falls back when that element carries the shopify-block classes:

          if ("FORM" == this.parentEl.nodeName) form = this.parentEl;

        So the script is moved into a plain <form> of the theme's own, holding nothing but a hidden
        id input that syncStockNotifyVariant keeps pointed at the popup's selection. The app reads
        that input, binds its change listeners to it, and renders into the block where it belongs.
        It is the only variant source the app can see from there, so a future version that stopped
        honouring it would draw nothing rather than draw the wrong thing — and drawing nothing is
        covered: the theme's own link out to the product page takes over (see quick-add.css).
      */
      /*
        The wishlist app ("Mst: Wishlist Prime"), made to work inside the popup.

        The app wires its heart buttons once, in iWish.init() on page load: it binds a click
        listener to every .iWishAdd it finds and copies the selected variant onto each from the
        page's [name=id] inputs. The popup's heart arrives later, inside fetched markup, so it had
        no listener and clicking it did nothing. (The app's own body-click fallback cannot cover it:
        its proxy calls this.iwishOnBodyClick() with the proxy handler as `this`, so it throws.)

        So the popup repeats those two steps for its own heart, through the same public methods the
        app's init() uses. checkIwish() climbs from the popup's variant input to the nearest
        .iWishAdd, which is the popup's own, never the page's; it also restores the filled state
        when the variant is already saved. product-info.js dispatches change on that input after
        every variant switch (dispatchVariantInputChange), which keeps the heart on the selection.
        The listeners go with the markup when hide() empties the modal.
      */
      setupWishlist() {
        const wishlist = window.iWish;
        if (!wishlist || typeof wishlist.iwishAddClick !== 'function') return;

        const buttons = this.modalContent.querySelectorAll('.iWishAdd');
        if (!buttons.length) return;

        const addLabel = window.iWishData && window.iWishData.iwish_add_txt;
        buttons.forEach((button) => {
          if (addLabel && button.innerHTML.trim() === '') button.innerHTML = addLabel;
          wishlist.iwishAddClick(button);
        });

        const variantInput = this.modalContent.querySelector('product-info input[name="id"]');
        if (!variantInput || typeof wishlist.checkIwish !== 'function') return;

        wishlist.checkIwish(variantInput);
        variantInput.addEventListener('change', () => wishlist.checkIwish(variantInput));
      }

      setupStockNotify(responseHTML) {
        this.teardownStockNotify();

        const block = this.modalContent.querySelector('.pi-app-block--stock-notify');
        const appScript = block?.querySelector('script[data-app]');
        const variantInput = this.modalContent.querySelector('product-info input[name="id"]');

        if (!block || !appScript || !variantInput) return;

        const bridge = document.createElement('form');
        bridge.className = 'pi-stock-notify-bridge';

        // Deliberately actionless: this form exists to be read, not submitted. Once the app has
        // rendered, its own fields sit inside it, and an Enter key must not navigate the page
        // behind the modal.
        bridge.addEventListener('submit', (event) => event.preventDefault());

        const bridgeInput = document.createElement('input');
        bridgeInput.type = 'hidden';
        bridgeInput.name = 'id';
        bridgeInput.value = variantInput.value;

        // init() inserts the app's mount point directly before its own script tag, so moving the
        // script in here is also what puts the rendered form inside the block.
        bridge.append(bridgeInput, appScript);
        block.appendChild(bridge);

        /*
          Whether the app has drawn anything is watched rather than sampled. Checking on a timer
          meant racing it — its first render waits on a settings request, and a check that landed
          just before it finished left the theme's fallback button showing underneath the very form
          it stands in for. Height is the signal: the app leaves its mount point in place even when
          it has decided not to draw into it.
        */
        const observer = new ResizeObserver(() =>
          this.modalContent.classList.toggle(
            'quick-add-modal__content-info--no-bis',
            block.getBoundingClientRect().height === 0
          )
        );

        observer.observe(block);

        this.stockNotify = { block, bridgeInput, observer };

        this.bootStockNotifyApp(responseHTML);
      }

      teardownStockNotify() {
        this.stockNotify?.observer.disconnect();
        this.stockNotify = null;
        this.modalContent?.classList.remove('quick-add-modal__content-info--no-bis');
      }

      syncStockNotifyVariant(variant) {
        if (!this.stockNotify || !variant?.id) return;

        this.stockNotify.bridgeInput.value = variant.id;
        dispatchVariantInputChange(this.stockNotify.bridgeInput);
      }

      bootStockNotifyApp(responseHTML) {
        const srcOf = (root, pattern) =>
          Array.from(root.querySelectorAll('script[src]'))
            .map((script) => script.getAttribute('src'))
            .find((src) => pattern.test(src));

        const loaderSrc = srcOf(responseHTML, /sc-bis|out-of-stock|back-in-stock/i);

        if (!loaderSrc) return;

        this.runStockNotifyScript(loaderSrc);

        // First boot also costs the app a settings request, so give it more room than a variant
        // change needs. If the loader declined to re-inject its bundle, run the bundle itself —
        // by then it is in the document and has no guard of its own.
        setTimeout(() => {
          if (!this.stockNotify || this.stockNotifyRendered()) return;

          // Drawing nothing is the right answer on a buyable selection, and re-running the bundle
          // to be told so again costs a settings request on every open.
          if (!this.modalContent.querySelector('.product-info-wrapper.pi-info--variant-unavailable')) return;

          const bundleSrc = srcOf(document, /back-in-stock/i);

          if (bundleSrc && bundleSrc !== loaderSrc) this.runStockNotifyScript(bundleSrc);
        }, 900);
      }

      runStockNotifyScript(src) {
        // The app's own "already loaded" flag would turn this into a no-op, and every open needs a
        // fresh run: the modal's DOM, mount point and all, is thrown away on close.
        delete window.__escBisLoaded;

        const script = document.createElement('script');

        script.src = `${src}${src.includes('?') ? '&' : '?'}qa=${Date.now()}`;
        document.head.appendChild(script);
      }

      stockNotifyRendered() {
        return this.stockNotify?.block.getBoundingClientRect().height > 0;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.linkProductTitle(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      /*
        In the popup the title doubles as the way out to the full product page — the modal is a
        summary, not the page. On the product page itself the <h1> stays plain text (linking a
        page to itself is noise), so the anchor is added here rather than in main-product.liquid.

        preprocessHTML also runs on every variant re-render (product-info:loaded), hence the
        early return when the anchor is already there.
      */
      linkProductTitle(productElement) {
        const title = productElement.querySelector('.pi-title');
        const productUrl = productElement.dataset.url;
        if (!title || !productUrl || title.querySelector('.pi-title__link')) return;

        const link = document.createElement('a');
        link.className = 'pi-title__link';
        link.href = productUrl;
        link.append(...title.childNodes);
        title.appendChild(link);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Breadcrumbs belong to the standalone product page, not the quick-add popup.
        const breadcrumbs = productElement.querySelector('.cl-breadcrumbs');
        if (breadcrumbs) breadcrumbs.remove();

        // Only the zoom trigger goes: it opens the lightbox, which needs the product-modal
        // and swiper assets the popup doesn't load. The thumbnail strip stays (see
        // onThumbnailClick for how it's driven without media-gallery.js). This runs on the
        // initial fetch only — a variant change brings in raw server slides via
        // product-info.js's updateMedia(), which skips this preprocessing, so quick-add.css
        // hides .pi-gallery__zoom unconditionally to cover that path too.
        const zoomButtons = productElement.querySelectorAll('.pi-gallery__zoom');
        zoomButtons.forEach((button) => button.remove());

        // Descriptive blocks — the feature list, the brand card, any rich text, the trust row —
        // belong to the full product page. The popup is a buy-now summary, and the title now
        // links out for the rest. The "Complete your stack" upsell goes too: its href is
        // #bundle-save, a target that only exists on the product page, so in the popup it
        // would scroll the page behind the modal to nothing.
       const descriptiveBlocks = productElement.querySelectorAll(
  '.pi-card.pi-brand, .pi-richtext, .pi-upsell'
);
descriptiveBlocks.forEach((block) => block.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
